import React, { useState } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { X, Check, Copy, Share2, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ReferralStatus, inviteLink } from '../lib/referral';

interface Props {
  referral: ReferralStatus;
  onClose: () => void;
  onUpgrade: () => void;
  premiumProcessing: boolean;
  currencySymbol: string;
  /** Optional copy overrides (used from the guesses card). */
  title?: string;
  headline?: string;
  note?: string;
}

const daysLeft = (iso: string) => Math.max(1, Math.ceil((new Date(iso).getTime() - Date.now()) / 86400000));

// "Have they opened it?" — the free route (bring 3 friends) next to the paid one
// (Premium). Same card style as the rest of the app.
export default function OpenedUnlockModal({ referral, onClose, onUpgrade, premiumProcessing, currencySymbol, title, headline, note }: Props) {
  const [copied, setCopied] = useState(false);
  const link = referral.code ? inviteLink(referral.code) : '';
  const active = !!referral.window_until;
  const earned = !active && referral.credits > 0;
  const filled = earned ? 3 : referral.progress;
  const message =
    `Got something you want to send me or someone else anonymously? 👀\n\n` +
    `No need to wait for their NGL link 😂\n\n` +
    `Send it through Yhuu now.\n` +
    `They'll never know it's from you. 🤫\n\n` +
    link;

  // Copies the whole invite message (ending with their link) so it can be pasted
  // straight into any chat. Falls back to the old-school copy trick for browsers
  // that block the clipboard API (some in-app browsers).
  const copy = async () => {
    let ok = false;
    try { await navigator.clipboard.writeText(message); ok = true; } catch { /* try fallback */ }
    if (!ok) {
      try {
        const el = document.createElement('textarea');
        el.value = message;
        el.setAttribute('readonly', '');
        el.style.cssText = 'position:fixed;top:0;left:0;opacity:0;font-size:16px';
        document.body.appendChild(el);
        el.select();
        el.setSelectionRange(0, message.length);
        ok = document.execCommand('copy');
        document.body.removeChild(el);
      } catch { /* ignore */ }
    }
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const share = async () => {
    try {
      if ((navigator as any).share) {
        await (navigator as any).share({ text: message });
        return;
      }
    } catch { return; } // they closed the share sheet
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, '_blank');
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}
      >
        <div className="absolute inset-0" style={{ background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)' }} onClick={onClose} />

        <motion.div
          className="relative w-full max-w-sm rounded-3xl overflow-hidden shadow-2xl"
          style={{ maxHeight: '92vh', overflowY: 'auto', background: '#fff' }}
          initial={{ opacity: 0, y: 30, scale: 0.97 }} animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ type: 'spring', stiffness: 340, damping: 30 }}
        >
          <div className="relative text-center" style={{ background: 'linear-gradient(90deg, #e11d48, #fb7185)', padding: '22px 56px' }}>
            <p style={{ color: '#fff', fontWeight: 900, fontSize: 20, letterSpacing: -0.3 }}>{title ?? 'Have they opened it?'}</p>
            <button
              onClick={onClose} aria-label="Close"
              style={{ position: 'absolute', top: '50%', right: 14, transform: 'translateY(-50%)', width: 36, height: 36, borderRadius: '50%', background: 'rgba(255,255,255,0.3)', border: 'none', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
            >
              <X size={16} color="#fff" />
            </button>
          </div>

          <div style={{ padding: '26px 24px 24px', textAlign: 'center', color: '#18181b' }}>
            {active ? (
              <>
                <div style={{ display: 'inline-block', padding: '8px 16px', borderRadius: 999, background: '#ecfdf5', color: '#047857', fontWeight: 900, fontSize: 14, marginBottom: 14 }}>
                  ✅ Unlocked · {daysLeft(referral.window_until!)} day{daysLeft(referral.window_until!) === 1 ? '' : 's'} left
                </div>
                <p style={{ fontSize: 15, lineHeight: 1.5, color: '#3f3f46', fontWeight: 600 }}>
                  Every session you send until then shows if it's been opened and who they guess, and keeps showing it after.
                </p>
              </>
            ) : earned ? (
              <>
                <div style={{ fontSize: 26, fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.5 }}>You earned it! 🎉</div>
                <p style={{ fontSize: 15, lineHeight: 1.5, color: '#3f3f46', fontWeight: 600, margin: '10px 0 16px' }}>
                  Your 3 days start when you send your next session.
                </p>
                <Link to="/create" onClick={onClose} style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '18px', borderRadius: 999, background: 'linear-gradient(90deg, #e11d48, #ec4899)', color: '#fff', fontWeight: 900, fontSize: 16, textDecoration: 'none' }}>
                  Send a session now
                </Link>
              </>
            ) : (
              <>
                <div style={{ fontSize: 24, fontWeight: 900, lineHeight: 1.2, letterSpacing: -0.5 }}>
                  {headline ?? 'Share Yhuu with 3 friends, see who opened it'}
                </div>
                <p style={{ fontSize: 14.5, lineHeight: 1.5, color: '#52525b', fontWeight: 600, margin: '10px 0 18px' }}>
                  When 3 friends sign up through your link and send a session, every session you send for 3 days shows if it's been opened and who they guess.
                </p>

                <div style={{ display: 'flex', justifyContent: 'center', gap: 12, marginBottom: 8 }}>
                  {[0, 1, 2].map(i => (
                    <div key={i} style={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: i < filled ? 'linear-gradient(135deg, #e11d48, #ec4899)' : '#fce7ef', border: i < filled ? 'none' : '2px dashed #f9a8c0' }}>
                      {i < filled && <Check size={20} color="#fff" strokeWidth={3} />}
                    </div>
                  ))}
                </div>
                <div style={{ fontSize: 13, fontWeight: 800, color: '#71717a', marginBottom: 18 }}>
                  {referral.progress} of 3 friends counted
                </div>
                {(referral.pending ?? 0) > 0 && (
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#be123c', margin: '-10px 0 16px' }}>
                    {referral.pending} more joined. They count once they send their first session.
                  </div>
                )}

                <button
                  onClick={share} data-track="unlock_share"
                  style={{ width: '100%', padding: '18px', borderRadius: 999, border: 'none', cursor: 'pointer', background: 'linear-gradient(90deg, #e11d48, #ec4899)', color: '#fff', fontWeight: 900, fontSize: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, boxShadow: '0 10px 24px rgba(225,29,72,0.3)' }}
                >
                  <Share2 size={18} color="#fff" /> Share Yhuu with friends
                </button>
                <button
                  onClick={copy} data-track="unlock_copy_link"
                  style={{ marginTop: 10, width: '100%', padding: '14px', borderRadius: 999, cursor: 'pointer', background: '#fff7fa', border: '1.5px solid #fbcfe0', color: '#be123c', fontWeight: 800, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                >
                  {copied ? <><Check size={16} /> Link copied!</> : <><Copy size={16} /> Copy link</>}
                </button>
                <div style={{ marginTop: 8, fontSize: 12, fontWeight: 700, color: '#a1a1aa', wordBreak: 'break-all' }}>{link.replace('https://', '')}</div>
                <p style={{ fontSize: 11.5, color: '#a1a1aa', fontWeight: 600, marginTop: 10 }}>
                  A friend counts once they sign up through your link and send their first session.
                </p>
                {note && (
                  <p style={{ fontSize: 12, color: '#be123c', fontWeight: 700, marginTop: 8 }}>{note}</p>
                )}
              </>
            )}

            {!active && (
              <>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 14px', color: '#a1a1aa', fontSize: 12, fontWeight: 800 }}>
                  <div style={{ flex: 1, height: 1, background: '#f1e4ea' }} /> OR <div style={{ flex: 1, height: 1, background: '#f1e4ea' }} />
                </div>
                <button
                  onClick={onUpgrade} disabled={premiumProcessing} data-track="unlock_premium"
                  style={{ width: '100%', padding: '15px', borderRadius: 999, cursor: 'pointer', background: '#fffbeb', border: '1.5px solid #fcd34d', color: '#b45309', fontWeight: 900, fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: premiumProcessing ? 0.6 : 1 }}
                >
                  <Crown size={16} /> {premiumProcessing ? 'Processing...' : `Unlock now · Premium ${currencySymbol}20/mo`}
                </button>
              </>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
